import { Suspense } from 'react';
import { LoadingPage } from '@/components/loading-page';
import { AlertBanner } from '@/components/alert-banner';
import { validateRouteRole, type GuardContext } from '@/lib/routing';
import { isBookViewerSupported } from '@/lib/device-utils';
import { isFamilyStudent, isStudent } from '@airreading/common';
import { type LoggedInUser } from '@airreading/common/data';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { maxBookPartForStudentGrade, StudentYearSelectable } from '@airreading/common/data';
import { useSession } from '@/auth/useSession';

export const Route = createFileRoute('/_layout/books/part/$partId')({
  beforeLoad: (ctx) => {
    validateRouteRole(isStudent)(ctx);
    const { actingUser } = (ctx as GuardContext).context;
    if (actingUser && isFamilyStudent(actingUser as LoggedInUser)) {
      throw redirect({ to: '/' });
    }
  },
  component: Page,
});

function Page() {
  return (
    <Suspense fallback={<LoadingPage />}>
      <PartDetail />
    </Suspense>
  );
}

function PartDetail() {
  const { actingUser } = useSession();
  const { partId } = Route.useParams();
  const navigate = Route.useNavigate();

  const grade = (actingUser.grade ?? 'null') as StudentYearSelectable;
  const maxBookLevel = maxBookPartForStudentGrade(grade);
  const index = Number(partId || '1');

  if (index > maxBookLevel) {
    navigate({
      to: '/books/part/$partId',
      params: { partId: String(maxBookLevel) },
    });
  }

  const urlPages: Record<number, string> = {
    1: 'https://heyzine.com/shelf/3ca08810df.html',
    2: 'https://heyzine.com/shelf/34a55cb370.html',
    3: 'https://heyzine.com/shelf/1bc0a4845d.html',
    4: 'https://heyzine.com/shelf/59d58eedf6.html',
    5: 'https://heyzine.com/shelf/fa2d1d1d82.html',
    6: 'https://heyzine.com/shelf/8cb3107cf7.html',
  };
  const url = urlPages[index];

  // Viewer uses pdf.js v5, which does not support Safari less than 16.4.
  if (!isBookViewerSupported()) {
    return (
      <div className="flex h-full w-full items-center justify-center p-4">
        <div className="max-w-md">
          <AlertBanner
            title="This book can’t be opened on your device"
            content="Your browser is not supported, please update your browser or OS and try again."
          />
        </div>
      </div>
    );
  }

  return <iframe allowFullScreen={true} src={url} className="h-full w-full"></iframe>;
}
